import NoData from '@/components/common/NoData'
import SearchInput from '@/components/common/SearchInput'
import StatusInterface from '@/components/common/StatusInterface'
import TableFooter from '@/components/common/Table/TableFooter'
import ButtonField from '@/components/form-control/ButtonField'
import { CharacterPlusIcon } from '@/components/icons'
import { SortBy, flyInVariantsDashboard } from '@/constant/common'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import { SearchParams } from '@/types'
import { usePagination } from '@mui/lab'
import { CircularProgress, SelectChangeEvent, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import { useConfigUI } from '../../context'
import { useSearchEtZone } from '../../hooks/etZone'
import EtzTable from './EtzTable'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'

type Props = {}

const EtZone = (props: Props) => {
  const { t } = useTranslation()
  
  const navigate = useNavigate()
  const location = useLocation()
  const MotionDiv = motion.div
  const { openSidebar, setSidebarView } = useConfigUI()
  const { openModal, setModalView } = useConfigUI()
  const { interfaceId, statusInterfaceId } = useAuthContext()
  const { data: statusInterface } = useSearchInterfaceId(interfaceId+"")
  const { isViewOnly } = usePermission()

  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const { data, refetch } = useSearchEtZone(interfaceId, searchParams)

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const page = parseInt(event.target.value, 10)
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: page,
      PageIndex: 1,
    })
  }

  const handleSearch = debounce((event: SelectChangeEvent) => {
    const value = event.target.value.toString().trim()
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      Keyword: value,
      PageIndex: 1,
    })
  }, 500)

  const { items } = usePagination({
    count: data?.totalPage,
    page: currentPage,
    onChange(_event: any, page: any) {
      setCurrentPage(page)
      setSearchParams({
        ...searchParams,
        PageIndex: page,
      })
    },
  })

  const handleAddEtZone = () => {
    // ViewOnly users cannot add new microclimate
    if (isViewOnly) return

    openSidebar()
    setSidebarView({ type: 'ETZ_VIEW' })
  }

  const handleEditZone = (id: string) => {
    openSidebar()
    setSidebarView({ type: 'ETZ_VIEW', id })
  }

  const handleDeleteItem = (id: string, interfaceId: string) => {
    if (isViewOnly) return
    openModal()
    setModalView({
      type: 'DELETE_ETZ_VIEW',
      id: id,
      interfaceId: interfaceId,
      onSuccess: async () => {
        await refetch()
      },
    })
  }

  useEffect(() => {
    const searchParamsFromURL = new URLSearchParams(location.search)

    // Extract values from URL and update searchParams
    const updatedSearchParams: SearchParams = {
      SortBy: searchParamsFromURL.get('SortBy') || undefined,
      SortDirection: searchParamsFromURL.get('SortDirection') || undefined,
      // Add other properties as needed
    }

    setSearchParams((prevSearchParams) => ({
      ...prevSearchParams,
      ...updatedSearchParams,
    }))
  }, [location.search])

  const handleDesc = (sortBy: SortBy) => {
    setSearchParams({
      ...searchParams,
      SortBy: sortBy,
      SortDirection: 'desc',
    })

    const newSearchParams = {
      SortBy: sortBy,
      SortDirection: 'desc',
    }

    const combinedSearchParams = { ...newSearchParams }

    // Convert the combined search params to a query string
    const queryString = new URLSearchParams(combinedSearchParams).toString()

    // Navigate to the new URL
    navigate(`?${queryString}`)
    return
  }

  const handleEsc = (sortBy: SortBy) => {
    setSearchParams({
      ...searchParams,
      SortBy: sortBy,
      SortDirection: 'asc',
    })

    const newSearchParams = {
      SortBy: sortBy,
      SortDirection: 'asc',
    }

    const combinedSearchParams = { ...newSearchParams }

    // Convert the combined search params to a query string
    const queryString = new URLSearchParams(combinedSearchParams).toString()

    // Navigate to the new URL
    navigate(`?${queryString}`)
    return
  }
  return (
    <ConfigLayout
      title="Micro Climate"
      header={<ConfigHeader subtitle={t('dashboardFooterDataConfig')} title={t('microClimate')} />}
    >
      <Stack
        className={`${classes.wrapper} ${
          statusInterface &&
          statusInterface.state === 'Offline' &&
          statusInterfaceId &&
          'wrapper-disabled'
        }`}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb="12px"
        >
          <MotionDiv
            initial="initialRight"
            animate="animate2"
            variants={flyInVariantsDashboard}
          >
            <SearchInput name={t('SearchMicroClimate')} onChange={handleSearch} />
          </MotionDiv>

          <MotionDiv
            initial="initialLeft"
            animate="animate2"
            variants={flyInVariantsDashboard}
          >
            <ButtonField
              startIcon={<CharacterPlusIcon />}
              style={{
                height: '32px',
                minWidth: '89px',
                borderRadius: '5px',
                opacity: isViewOnly ? 0.6 : 1,
              }}
              name={t('commonActionsAddNew')}
              onClick={handleAddEtZone}
              disabled={isViewOnly}
            />
          </MotionDiv>
        </Stack>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
          className={classes.content}
        >
          {!data && (
            <Stack
              sx={{
                position: 'absolute',
                transform: 'translate(-50%, -50%)',
                left: '50%',
                top: '35%',
              }}
            >
              <CircularProgress />
            </Stack>
          )}

          {data && data?.list.length > 0 && (
            <EtzTable
              rows={data.list}
              onEditItem={handleEditZone}
              onDeleteItem={handleDeleteItem}
              handleDesc={handleDesc}
              handleEsc={handleEsc}
            />
          )}

          {data && data.totalCount === 0 && <NoData text={ t('noData') } />}
        </MotionDiv>
        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
        >
          <Stack
            sx={{
              display: 'flex',
              bottom: '0',
              justifyContent: 'end',
              flexGrow: '1',
            }}
          >
            <TableFooter
              page={data?.page}
              totalCount={data?.totalCount}
              totalPage={data?.totalPage}
              rowsPerPage={data?.size ?? 0}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              items={items}
            />
          </Stack>
        </MotionDiv>
      </Stack>
      <StatusInterface />
    </ConfigLayout>
  )
}

export default EtZone
